import React from 'react';

const NeuralNetIcon = ({ color }) => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
    <g stroke={color} strokeWidth="1.2" opacity="0.5">
      <line x1="10" y1="12" x2="22" y2="17" />
      <line x1="10" y1="22" x2="22" y2="17" />
      <line x1="10" y1="32" x2="22" y2="17" />
      <line x1="10" y1="12" x2="22" y2="27" />
      <line x1="10" y1="22" x2="22" y2="27" />
      <line x1="10" y1="32" x2="22" y2="27" />
      <line x1="22" y1="17" x2="34" y2="22" />
      <line x1="22" y1="27" x2="34" y2="22" />
    </g>
    <g fill={color}>
      <circle cx="10" cy="12" r="2.5" />
      <circle cx="10" cy="22" r="2.5" />
      <circle cx="10" cy="32" r="2.5" />
      <circle cx="22" cy="17" r="2.5" />
      <circle cx="22" cy="27" r="2.5" />
      <circle cx="34" cy="22" r="3" />
    </g>
  </svg>
);

const DigitalBadgeIcon = ({ color }) => {
  const cx = 22, cy = 17, n = 18, outerR = 15, innerR = 13;
  const seal = Array.from({ length: n * 2 }, (_, i) => {
    const a = (i / (n * 2)) * 2 * Math.PI - Math.PI / 2;
    const r = i % 2 === 0 ? outerR : innerR;
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join('L');
  return (
    <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
      <path d={`M${seal}Z`} fill={color} opacity="0.13" />
      <path d={`M${seal}Z`} stroke={color} strokeWidth="0.7" opacity="0.5" fill="none" />
      <circle cx={cx} cy={cy} r="9" stroke={color} strokeWidth="1" opacity="0.55" />
      <polyline
        points={`${cx - 4.5},${cy} ${cx - 1},${cy + 4} ${cx + 5},${cy - 5}`}
        stroke={color} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none"
      />
      <rect x="12" y="28" width="20" height="5.5" rx="1" fill={color} opacity="0.75" />
      <polygon points="12,33.5 9,41 15,33.5" fill={color} opacity="0.55" />
      <polygon points="32,33.5 35,41 29,33.5" fill={color} opacity="0.55" />
    </svg>
  );
};

const SixSigmaIcon = ({ color }) => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none" aria-hidden="true">
    <circle cx="22" cy="22" r="19" fill={color} opacity="0.1" />
    <circle cx="22" cy="22" r="19" stroke={color} strokeWidth="1" opacity="0.45" fill="none" />
    <text x="11" y="31" fontFamily="Georgia, serif" fontSize="24" fontWeight="800" fill={color} opacity="0.85">6</text>
    <text x="25" y="18" fontFamily="Arial, sans-serif" fontSize="7" fontWeight="600" fill={color} opacity="0.65">lean</text>
    <text x="22" y="35" fontFamily="Georgia, serif" fontSize="18" fontWeight="700" fill={color} opacity="0.75">σ</text>
  </svg>
);

const ICONS = [NeuralNetIcon, DigitalBadgeIcon, SixSigmaIcon];
const INK = '#1A2238';
const ICON_COLORS = ['#1A2238', '#1A2238', '#1A2238'];

export default function Certifications({ t }) {
  return (
    <section id="certifications" className="max-w-content mx-auto px-4 md:px-8 pt-6 pb-4">
      <div className="mb-6">
        <h2
          className="font-display font-medium text-ink m-0"
          style={{ fontSize: 'clamp(32px, 4vw, 48px)', letterSpacing: '-0.02em', lineHeight: 1.05 }}
        >
          {t.certifications.title}
        </h2>
        <div className="font-body text-[14px] text-ink-3 mt-1.5">{t.certifications.sub}</div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        {t.certifications.items.map((c, i) => {
          const Icon = ICONS[i] || ICONS[0];
          const Tag = c.verifiable ? 'a' : 'div';
          const interactiveProps = c.verifiable
            ? { href: c.href, target: '_blank', rel: 'noopener noreferrer' }
            : {};

          return (
            <Tag
              key={i}
              {...interactiveProps}
              className={`group flex flex-col gap-0 bg-surface border border-rule rounded-card p-[26px_24px] relative min-h-[200px] text-ink no-underline transition-all duration-200 ${
                c.verifiable ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgba(26,34,56,0.18)] hover:border-ink-3' : 'cursor-default'
              }`}
              style={{ display: 'flex', flexDirection: 'column' }}
            >
              <div
                className="font-display font-medium text-ink mb-[18px]"
                style={{ fontSize: 18, lineHeight: 1.22, letterSpacing: '-0.01em' }}
              >
                {c.name}
              </div>
              <div className="flex items-center justify-between gap-3 mt-auto">
                <div style={{ color: ICON_COLORS[i] || INK, lineHeight: 0 }}>
                  <Icon color={ICON_COLORS[i] || INK} />
                </div>
                {c.verifiable ? (
                  <span className="font-mono text-[11px] text-ink-2 border border-rule px-2.5 py-[5px] rounded-full inline-flex items-center gap-1.5 tracking-[0.04em] bg-bg">
                    verify <span aria-hidden="true">↗</span>
                  </span>
                ) : (
                  <span className="font-mono text-[11px] text-ink-3 border border-dashed border-rule px-2.5 py-[5px] rounded-full tracking-[0.04em]">
                    credential
                  </span>
                )}
              </div>
            </Tag>
          );
        })}
      </div>
    </section>
  );
}
